import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, XCircle, Loader2 } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";

const ESTADO_LABEL = {
  nuevo: { txt: "Nuevo", color: "text-emerald-400 border-emerald-700/40" },
  duplicado_cif: { txt: "Duplicado CIF", color: "text-amber-400 border-amber-700/40" },
  duplicado_factusol: { txt: "Mismo cód.", color: "text-blue-400 border-blue-700/40" },
  sin_cif: { txt: "Sin CIF", color: "text-zinc-400 border-zinc-700/40" },
};

export function ImportFactusolDialog({ open, onOpenChange, onDone }) {
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null); // {total, counters, items}
  const [committing, setCommitting] = useState(false);

  const reset = () => { setPreview(null); if (fileRef.current) fileRef.current.value = ""; };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/clientes/import-factusol/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Acción por defecto según estado
      const items = data.items.map((it) => ({
        ...it,
        _accion: it._estado === "nuevo" || it._estado === "sin_cif" ? "crear"
                : it._estado === "duplicado_cif" || it._estado === "duplicado_factusol" ? "omitir"
                : "crear",
      }));
      setPreview({ ...data, items });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo analizar el archivo");
      reset();
    } finally { setLoading(false); }
  };

  const setAccion = (idx, accion) => {
    setPreview((p) => ({ ...p, items: p.items.map((it, i) => i === idx ? { ...it, _accion: accion } : it) }));
  };

  const bulkAccion = (filtro, accion) => {
    setPreview((p) => ({ ...p, items: p.items.map((it) => filtro(it) ? { ...it, _accion: accion } : it) }));
  };

  const commit = async () => {
    if (!preview) return;
    const aImportar = preview.items.filter((it) => it._accion !== "omitir");
    if (aImportar.length === 0) return toast.error("No hay nada que importar (todos marcados como omitir)");
    setCommitting(true);
    try {
      const { data } = await api.post("/clientes/import-factusol/commit", { items: preview.items });
      toast.success(`✅ Importación: ${data.creados} creados · ${data.actualizados} actualizados · ${data.omitidos} omitidos`);
      if (data.errores?.length) toast.warning(`${data.errores.length} errores — ver consola`);
      if (data.errores?.length) console.warn("Errores import:", data.errores);
      onDone?.();
      onOpenChange(false);
      reset();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error en la importación");
    } finally { setCommitting(false); }
  };

  const totalImportar = preview?.items?.filter((it) => it._accion !== "omitir").length || 0;

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-6xl max-h-[92vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-amber-400" /> Importar clientes desde Factusol
          </DialogTitle>
        </DialogHeader>

        {!preview && (
          <div className="flex flex-col items-center justify-center p-10 border-2 border-dashed border-zinc-800 rounded-lg">
            <Upload className="w-10 h-10 text-zinc-500 mb-3" />
            <p className="text-sm text-zinc-300 mb-1">Sube el XLS exportado de Factusol "Listado de fichas de clientes"</p>
            <p className="text-xs text-zinc-500 mb-4">Formato `.xls` legacy (Excel 97-2003). No `.xlsx`.</p>
            <input ref={fileRef} type="file" accept=".xls" onChange={onFile} className="hidden" id="factusol-file"
                   data-testid="factusol-file-input" />
            <label htmlFor="factusol-file" className="cursor-pointer">
              <Button asChild className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold"
                      disabled={loading} data-testid="factusol-select-btn">
                <span>{loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analizando…</> : "Seleccionar archivo XLS"}</span>
              </Button>
            </label>
          </div>
        )}

        {preview && (
          <>
            <div className="flex flex-wrap gap-3 items-center justify-between bg-zinc-900/60 border border-zinc-800 rounded-lg p-3">
              <div className="flex flex-wrap gap-3 text-sm">
                <KPI label="Total" value={preview.total} color="text-zinc-100" />
                <KPI label="Nuevos" value={preview.counters.nuevo} color="text-emerald-400" />
                <KPI label="Duplicados CIF" value={preview.counters.duplicado_cif} color="text-amber-400" />
                <KPI label="Sin CIF" value={preview.counters.sin_cif} color="text-zinc-400" />
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="bg-transparent border-zinc-700 text-xs"
                        onClick={() => bulkAccion((it) => it._estado === "duplicado_cif", "actualizar")}
                        data-testid="factusol-bulk-update-cif">
                  Duplicados → completar campos vacíos
                </Button>
                <Button size="sm" variant="outline" className="bg-transparent border-zinc-700 text-xs"
                        onClick={() => bulkAccion(() => true, "crear")}>
                  Todos → crear
                </Button>
                <Button size="sm" variant="outline" className="bg-transparent border-zinc-700 text-xs"
                        onClick={() => bulkAccion(() => true, "omitir")}>
                  Todos → omitir
                </Button>
              </div>
            </div>

            <div className="flex-1 overflow-auto border border-zinc-800 rounded-lg mt-3">
              <table className="w-full text-sm">
                <thead className="bg-zinc-900 text-zinc-400 text-xs sticky top-0">
                  <tr>
                    <th className="text-left p-2 w-12">Cód.</th>
                    <th className="text-left p-2">Nombre</th>
                    <th className="text-left p-2 w-32">CIF</th>
                    <th className="text-left p-2 w-32">Población</th>
                    <th className="text-left p-2 w-36">Teléfono / email</th>
                    <th className="text-left p-2 w-28">Estado</th>
                    <th className="text-left p-2 w-32">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.items.map((it, idx) => {
                    const meta = ESTADO_LABEL[it._estado] || ESTADO_LABEL.nuevo;
                    return (
                      <tr key={idx} className="border-t border-zinc-800/60 hover:bg-zinc-900/40">
                        <td className="p-2 text-zinc-500">{it.codigo_factusol}</td>
                        <td className="p-2">
                          <div className="font-medium text-zinc-100">{it.nombre}</div>
                          {it.direccion_fiscal && (
                            <div className="text-xs text-zinc-500 truncate max-w-md">{it.direccion_fiscal}</div>
                          )}
                        </td>
                        <td className="p-2 text-zinc-300">{it.cif || <span className="text-zinc-600">—</span>}</td>
                        <td className="p-2 text-zinc-400 text-xs">{it.direccion_fiscal?.split(",").slice(-2)[0]?.trim()}</td>
                        <td className="p-2 text-xs">
                          {it.telefono && <div className="text-zinc-300">{it.telefono}</div>}
                          {it.email_contacto && <div className="text-zinc-500 truncate max-w-[140px]">{it.email_contacto}</div>}
                        </td>
                        <td className="p-2">
                          <span className={`inline-block px-2 py-0.5 rounded border text-xs ${meta.color}`}>
                            {meta.txt}
                          </span>
                        </td>
                        <td className="p-2">
                          <Select value={it._accion} onValueChange={(v) => setAccion(idx, v)}>
                            <SelectTrigger className="bg-zinc-900 border-zinc-700 h-8 text-xs" data-testid={`factusol-accion-${idx}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-950 border-zinc-700 text-zinc-100">
                              <SelectItem value="crear">Crear</SelectItem>
                              <SelectItem value="actualizar" disabled={!it._match_id}>Completar campos vacíos</SelectItem>
                              <SelectItem value="omitir">Omitir</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        <DialogFooter className="mt-3">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
                  className="bg-transparent border-zinc-700">Cancelar</Button>
          {preview && (
            <Button onClick={commit} disabled={committing || totalImportar === 0}
                    className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold"
                    data-testid="factusol-commit-btn">
              {committing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importando…</>
                          : `Importar ${totalImportar} cliente${totalImportar === 1 ? "" : "s"}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KPI({ label, value, color }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-zinc-500 text-xs uppercase">{label}</span>
      <span className={`font-mono font-semibold ${color}`}>{value}</span>
    </div>
  );
}
